import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/shadcn/dialog';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import communityService, { GenerateGamesBulkPayload } from '@/services/communityService';

const MIN_FORMAT = 3;
const MAX_FORMAT = 15;
const DEFAULT_FUTSAL = 5;
const DEFAULT_MINI = 7;

const SORT_OPTIONS: { value: GenerateGamesBulkPayload['sort_by']; label: string }[] = [
  { value: 'join_time', label: 'Join Time' },
  { value: 'alphabet', label: 'Alphabet nama' },
  { value: 'random', label: 'Random' },
];

interface GenerateGameOtomatisModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventSlug: string;
  eventSportCategorySlug?: string;
  onSuccess: () => void;
  onError?: (message: string) => void;
}

export default function GenerateGameOtomatisModal({
  isOpen,
  onClose,
  eventSlug,
  eventSportCategorySlug = '',
  onSuccess,
  onError,
}: GenerateGameOtomatisModalProps) {
  const defaultFormat = eventSportCategorySlug === 'futsal' ? DEFAULT_FUTSAL : DEFAULT_MINI;
  const [sortBy, setSortBy] = useState<GenerateGamesBulkPayload['sort_by']>('join_time');
  const [playersPerTeam, setPlayersPerTeam] = useState(defaultFormat);
  const [numTeams, setNumTeams] = useState(4);
  const [numGames, setNumGames] = useState(2);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPlayersPerTeam(eventSportCategorySlug === 'futsal' ? DEFAULT_FUTSAL : DEFAULT_MINI);
      setFormError(null);
    }
  }, [isOpen, eventSportCategorySlug]);

  const numTeamsEven = numTeams % 2 === 0;
  const safeNumTeams = numTeamsEven ? numTeams : Math.max(2, numTeams - 1);

  useEffect(() => {
    const maxG = Math.floor(safeNumTeams / 2);
    if (numGames > maxG && maxG >= 1) setNumGames(maxG);
  }, [safeNumTeams]);

  const canSubmit =
    safeNumTeams >= 2 &&
    numGames >= 1 &&
    2 * numGames <= safeNumTeams &&
    playersPerTeam >= MIN_FORMAT &&
    playersPerTeam <= MAX_FORMAT;

  const handleNumTeamsChange = (v: number) => {
    const n = Math.max(0, Math.floor(v));
    if (n % 2 !== 0) {
      setNumTeams(n > 0 ? n - 1 : 2);
    } else {
      setNumTeams(n || 2);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setFormError(null);
    setSubmitting(true);
    try {
      const payload: GenerateGamesBulkPayload = {
        sort_by: sortBy,
        players_per_team: playersPerTeam,
        num_teams: safeNumTeams,
        num_games: numGames,
      };
      await communityService.generateGamesBulk(eventSlug, payload);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data &&
        typeof (err.response.data as { message: unknown }).message === 'string'
          ? (err.response.data as { message: string }).message
          : err instanceof Error
            ? err.message
            : 'Gagal generate jadwal, coba lagi';
      setFormError(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="lucide:sparkles" className="h-5 w-5 text-primary" />
            Generate Jadwal Otomatis
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="sort-by">Generate Berdasarkan</Label>
            <Select
              value={sortBy}
              onValueChange={(v) => setSortBy(v as GenerateGamesBulkPayload['sort_by'])}
            >
              <SelectTrigger id="sort-by">
                <SelectValue placeholder="Pilih opsi" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="players-per-team">Jumlah pemain dalam tim</Label>
            <Select
              value={String(playersPerTeam)}
              onValueChange={(v) => {
                const n = parseInt(v, 10);
                if (!Number.isNaN(n) && n >= MIN_FORMAT && n <= MAX_FORMAT) {
                  setPlayersPerTeam(n);
                }
              }}
            >
              <SelectTrigger id="players-per-team">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: MAX_FORMAT - MIN_FORMAT + 1 }, (_, i) => MIN_FORMAT + i).map(
                  (n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} vs {n} ({n} pemain)
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="num-teams">Jumlah Tim</Label>
            <Input
              id="num-teams"
              type="number"
              min={2}
              step={2}
              value={safeNumTeams}
              onChange={(e) => handleNumTeamsChange(parseInt(e.target.value, 10) || 0)}
            />
            <p className="text-xs text-muted-foreground">Harus genap</p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="num-games">Jumlah Game</Label>
            <Input
              id="num-games"
              type="number"
              min={1}
              max={Math.floor(safeNumTeams / 2)}
              value={numGames}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!Number.isNaN(n) && n >= 1) {
                  setNumGames(Math.min(n, Math.floor(safeNumTeams / 2)));
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              Sistem akan membuat <strong className="text-primary">{numGames} game</strong>{' '}
              sekaligus dengan format pemain yang sudah disesuaikan secara otomatis.
            </p>
          </div>
          {formError && (
            <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
              {formError}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Batal
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? 'Memproses...' : 'Generate Sekarang'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
